import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TeButton,
  TeContainer,
  TeContainerSlim,
  TeInputText,
  TeLabel,
  TeModal,
  TePopover,
  TeText,
} from '@topcon/tuicl-react';

import OrgCard from '../../components/OrgCard';
import IncomingOrganizationCard from '../../components/Migration/IncomingOrganizationCard';
import SelectAgreementStep from '../../components/ServiceAgreement/Customer/SelectAgreementStep';
import ReviewAgreementStep from '../../components/ServiceAgreement/Customer/ReviewAgreementStep';
import useServiceAgreement from '../../hooks/useServiceAgreement';
import { invalidateCustomerSuggestions } from '../../hooks/queries/migration.queries';
import { formatAddress } from '../../utils/formatAddress';
import { LinkedCustomerSuggestion } from './customerSuggestions.helpers';
import { ServiceAgreementTermsEnum } from '../../api/generated/pm';
import { zServiceAgreementWithCustomerPostBody } from '../../api/generated/pm/zod.gen';

const TOTAL_STEPS = 2;
/** The dealer's own reference for this customer — `providerCustomerNumber` on the API. */
const CUSTOMER_NUMBER_FIELD = 'providerCustomerNumber';
const CUSTOMER_NUMBER_MAX_LENGTH =
  zServiceAgreementWithCustomerPostBody.shape.customerNumber.unwrap().maxLength ?? undefined;

enum ConnectStep {
  SelectAgreement,
  ReviewAgreement,
}

interface Props {
  /** A suggestion already linked to a Business Partner, with its SA still pending. */
  suggestion: LinkedCustomerSuggestion;
  open: boolean;
  onClose: (shouldUpdate: boolean) => void;
}

/**
 * **Connect**: the two-step wizard that creates the Service Agreement between
 * the dealer and a Customer Suggestion that is already linked to a Business
 * Partner — the step that actually finishes a suggestion.
 *
 * Reached either straight from the card's **Review** button (an exact match has
 * no match to choose, so there is nothing to review) or as the hand-off from a
 * **Merge** whose accept response still reports `serviceAgreementStatus:
 * 'pending'`.
 *
 * Reuses the dealer customer-creation wizard's steps (`SelectAgreementStep`,
 * `ReviewAgreementStep`) rather than re-drawing them; only the read-only
 * organization card and the dealer's own customer-reference field are new.
 *
 * Callers mount this fresh per suggestion (`{target && <Connect… />}`) so the
 * step, the picked terms and the customer number reset between suggestions.
 */
function ConnectCustomerSuggestionModal({ suggestion, open, onClose }: Props) {
  const { t } = useTranslation('common', { keyPrefix: 'customerSuggestions' });
  const { t: tCommon } = useTranslation('common');

  const [step, setStep] = useState<ConnectStep>(ConnectStep.SelectAgreement);
  const [providerCustomerNumber, setProviderCustomerNumber] = useState('');
  const [agreementTerms, setAgreementTerms] = useState<ServiceAgreementTermsEnum>(
    ServiceAgreementTermsEnum.FULL_SERVICE
  );
  const [isEulaAccepted, setIsEulaAccepted] = useState(false);
  const [isAgreementAcknowledged, setIsAgreementAcknowledged] = useState(false);

  const {
    createServiceAgreementWithCustomer,
    isPendingCreateServiceAgreementWithCustomer: isPending,
  } = useServiceAgreement();

  const { businessPartner } = suggestion;
  const businessPartnerAddress = formatAddress(businessPartner.companyAddress);
  const suggestionAddress = formatAddress(suggestion.address);

  const handleConnect = async () => {
    try {
      await createServiceAgreementWithCustomer(
        {
          customerBusinessPartnerId: businessPartner.id,
          customerNumber: providerCustomerNumber.trim() || undefined,
          serviceAgreementTerms: agreementTerms,
        },
        businessPartner.name
      );
    } catch {
      // `useServiceAgreement` already surfaced the error via the error drawer
      // (or its fallback toast). Leave the wizard open on this step so the
      // entered data survives a retry.
      return;
    }
    invalidateCustomerSuggestions();
    onClose(true);
  };

  return (
    <TeModal
      variant="dark"
      open={open}
      closeMode="button"
      closeable={!isPending}
      modalTitle={t('connectModal.title')}
      footerText={tCommon('step.count', { current: step + 1, total: TOTAL_STEPS })}
      teclose={() => !isPending && onClose(false)}
      contentPadding={0}
      width="l"
    >
      <TeContainer
        backgroundColor="background.primary"
        padding="s"
        column
        grow
        fill
        gap="xs"
        overflow="auto"
      >
        {step === ConnectStep.SelectAgreement && (
          <>
            <IncomingOrganizationCard
              label={t('connectModal.linkedFromSuggestion')}
              name={suggestion.name}
              sourceSystem={suggestion.sourceSystem}
              address={suggestionAddress}
            />
            <TeText size={14} weight={500}>
              {t('connectModal.linkedToBusinessPartner')}
            </TeText>
            <OrgCard
              name={businessPartner.name}
              externalId={businessPartner.extId}
              address={businessPartnerAddress || undefined}
              baseBackground="primary"
              addressFill
              testId="connect-organization-card"
              footer={
                <TeContainerSlim column fill gap="xs">
                  <TeLabel
                    text={t('connectModal.customerNumberLabel')}
                    subtext={t('connectModal.customerNumberOptional')}
                    icon="info"
                    name={CUSTOMER_NUMBER_FIELD}
                  >
                    <TePopover variant="secondary" operation="hover" placement="top">
                      <TeContainerSlim padding="xs">
                        <TeText color="text.inverse" size={12}>
                          {t('connectModal.customerNumberHint')}
                        </TeText>
                      </TeContainerSlim>
                    </TePopover>
                  </TeLabel>
                  <TeInputText
                    name={CUSTOMER_NUMBER_FIELD}
                    maxLength={CUSTOMER_NUMBER_MAX_LENGTH}
                    size="s"
                    grow
                    placeholder={t('connectModal.customerNumberPlaceholder')}
                    value={providerCustomerNumber}
                    techange={(e) =>
                      setProviderCustomerNumber((e.target as HTMLInputElement).value)
                    }
                  />
                </TeContainerSlim>
              }
            />
            <SelectAgreementStep
              agreementType={agreementTerms}
              onSelectAgreement={setAgreementTerms}
              onAcknowledgedChange={setIsAgreementAcknowledged}
            />
          </>
        )}
        {step === ConnectStep.ReviewAgreement && (
          <ReviewAgreementStep onChecked={setIsEulaAccepted} />
        )}
      </TeContainer>

      <TeButton
        slot="tertiary"
        data-testid="connect-cancel"
        text={tCommon('cancel')}
        variant="neutral"
        size="s"
        disabled={isPending}
        teclick={() => !isPending && onClose(false)}
      />
      {step === ConnectStep.ReviewAgreement && (
        <TeButton
          slot="tertiary"
          data-testid="connect-previous-step"
          text={tCommon('step.previous')}
          variant="neutral"
          size="s"
          disabled={isPending}
          teclick={() => setStep(ConnectStep.SelectAgreement)}
        />
      )}
      {step === ConnectStep.SelectAgreement ? (
        <TeButton
          slot="primary"
          data-testid="connect-next-step"
          text={tCommon('step.next')}
          variant="primary"
          size="s"
          disabled={!isAgreementAcknowledged || isPending}
          teclick={() => setStep(ConnectStep.ReviewAgreement)}
        />
      ) : (
        <TeButton
          slot="primary"
          data-testid="connect-submit"
          text={t('connectModal.connect')}
          variant="primary"
          size="s"
          disabled={!isEulaAccepted || isPending}
          loading={isPending}
          teclick={handleConnect}
        />
      )}
    </TeModal>
  );
}

export default ConnectCustomerSuggestionModal;
